import { useState, useEffect, useCallback } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import { LevelBadge, MannerBadge } from "./Badges";
import { formatSportLevel, formatRecordLevel, formatRecord, formatManner } from "./utils/teamLevel";

const SPORTS = {
  tennis:      { icon: "🎾", label: "테니스" },
  soccer:      { icon: "⚽", label: "축구" },
  futsal:      { icon: "🥅", label: "풋살" },
  badminton:   { icon: "🏸", label: "배드민턴" },
  basketball:  { icon: "🏀", label: "농구" },
  bowling:     { icon: "🎳", label: "볼링" },
  tabletennis: { icon: "🏓", label: "탁구" },
  baseball:    { icon: "⚾", label: "야구" },
  golf:        { icon: "⛳", label: "골프" },
  running:     { icon: "🏸", label: "피클볼" },
  billiards:   { icon: "🎱", label: "당구" },
};

const STATUS_CONFIG = {
  pending:       { label: "신청 중",      color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  accepted:      { label: "교류전 확정",  color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  rejected:      { label: "상대 팀 거절", color: "#E8412A", bg: "#fff5f5", border: "#fecaca" },
  cancelled:     { label: "신청 취소됨",  color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" },
  auto_rejected: { label: "자동 거절됨",  color: "#9ca3af", bg: "#f3f4f6", border: "#d1d5db" },
};

const WEEK_DAYS = ["일", "월", "화", "수", "목", "금", "토"];
const SPORTS_LABEL_MAP = {
  tennis: "테니스", soccer: "축구", futsal: "풋살", badminton: "배드민턴",
  basketball: "농구", bowling: "볼링", table_tennis: "탁구", baseball: "야구",
  golf: "골프", pickleball: "피클볼", billiards: "당구",
};
const SPORTS_ICON_MAP = {
  tennis: "🎾", soccer: "⚽", futsal: "🥅", badminton: "🏸",
  basketball: "🏀", bowling: "🎳", table_tennis: "🏓", baseball: "⚾",
  golf: "⛳", pickleball: "🏸", billiards: "🎱",
};

function normalizeSportsType(type) {
  if (type === "tabletennis") return "table_tennis";
  if (type === "running")     return "pickleball";
  return type || "";
}

function formatSportDetail(rawType, cd) {
  if (!cd) return null;
  const t = normalizeSportsType(rawType);
  switch (t) {
    case "tennis":       return cd.ntrp ? `NTRP ${cd.ntrp}` : null;
    case "soccer":
    case "futsal":       return cd.position || null;
    case "badminton":    return cd.level || null;
    case "basketball":   return [cd.position, cd.level].filter(Boolean).join(" · ") || null;
    case "bowling":      return cd.avg ? `AVG ${cd.avg}` : null;
    case "table_tennis": return cd.level || null;
    case "baseball":     return cd.position || null;
    case "golf":         return cd.handicap ? `핸디캡 ${cd.handicap}` : null;
    case "pickleball":
    case "billiards":    return cd.years ? `구력 ${cd.years}년` : null;
    default:             return null;
  }
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("ko-KR", { month: "long", day: "numeric" }) + ` (${WEEK_DAYS[d.getDay()]})`;
}

function buildLocation(post) {
  return [post?.location_city, post?.location_district, post?.location_name]
    .filter(Boolean).join(" ") || "-";
}

function parseAgeGroup(ag) {
  if (!ag) return "";
  if (Array.isArray(ag)) return ag.join(", ");
  try { const p = JSON.parse(ag); return Array.isArray(p) ? p.join(", ") : ag; } catch { return ag; }
}

function parseArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; }
}

/* ── 메인 컴포넌트 ─────────────────────────────── */
export default function MatchEventDetail({ application, post, hostTeam, myTeam, onBack }) {
  const { user } = useAuth();

  const [attending,       setAttending]       = useState(false);
  const [attendLoading,   setAttendLoading]   = useState(false);
  const [myAttendees,     setMyAttendees]     = useState([]);
  const [myLoading,       setMyLoading]       = useState(false);
  const [hostAttendees,   setHostAttendees]   = useState([]);
  const [hostLoading,     setHostLoading]     = useState(false);
  const [selectedMember,  setSelectedMember]  = useState(null);
  const [memberProfile,   setMemberProfile]   = useState(null);
  const [memberSports,    setMemberSports]    = useState([]);
  const [memberLoading,   setMemberLoading]   = useState(false);
  // 팀 정보 모달 (우리 팀 / 상대 팀 공용)
  const [teamInfoTarget,  setTeamInfoTarget]  = useState(null); // { team, role }
  const [teamInfoData,    setTeamInfoData]    = useState(null); // { fullTeam, members }
  const [teamInfoLoading, setTeamInfoLoading] = useState(false);

  /* 내 참여 상태 조회 */
  useEffect(() => {
    if (!user?.id || !post?.id) return;
    supabase.from("attendance").select("id, status")
      .eq("post_id", post.id).eq("user_id", user.id).maybeSingle()
      .then(({ data }) => setAttending(data?.status === "attending"));
  }, [user?.id, post?.id]); // eslint-disable-line

  /* 팀별 참가자 목록 조회 헬퍼 */
  const fetchTeamAttendees = useCallback(async (teamId) => {
    if (!post?.id || !teamId) return [];
    const { data: rows } = await supabase.from("attendance")
      .select("user_id").eq("post_id", post.id).eq("team_id", teamId).eq("status", "attending");
    if (!rows || rows.length === 0) return [];
    const userIds    = rows.map(r => r.user_id);
    const normalType = normalizeSportsType(post.sports_type);
    const [{ data: profiles }, { data: sports }] = await Promise.all([
      supabase.from("user_profiles").select("id, nickname, profile_image, gender, is_profile_public").in("id", userIds),
      supabase.from("user_sports").select("user_id, career_detail, is_player").in("user_id", userIds).eq("sports_type", normalType),
    ]);
    const profileMap = Object.fromEntries((profiles || []).map(p => [p.id, p]));
    const sportMap   = Object.fromEntries((sports   || []).map(sp => [sp.user_id, sp]));
    return rows.map(r => ({
      user_id: r.user_id,
      profile: profileMap[r.user_id] || null,
      sport:   sportMap[r.user_id]   || null,
    }));
  }, [post?.id, post?.sports_type]); // eslint-disable-line

  /* 우리 팀 참가자 */
  useEffect(() => {
    if (!myTeam?.id) return;
    setMyLoading(true);
    fetchTeamAttendees(myTeam.id).then(d => { setMyAttendees(d); setMyLoading(false); });
  }, [myTeam?.id, attending, fetchTeamAttendees]);

  /* 상대 팀 참가자 */
  useEffect(() => {
    if (!hostTeam?.id) return;
    setHostLoading(true);
    fetchTeamAttendees(hostTeam.id).then(d => { setHostAttendees(d); setHostLoading(false); });
  }, [hostTeam?.id, attending, fetchTeamAttendees]);

  /* 선택된 팀원 프로필 조회 */
  useEffect(() => {
    if (!selectedMember) return;
    setMemberLoading(true);
    const normalType = normalizeSportsType(post?.sports_type || "");
    Promise.all([
      supabase.from("user_profiles").select("*").eq("id", selectedMember.user_id).maybeSingle(),
      supabase.from("user_sports").select("*").eq("user_id", selectedMember.user_id).eq("sports_type", normalType),
    ]).then(([{ data: fp }, { data: sp }]) => {
      setMemberProfile(fp || null);
      setMemberSports(sp || []);
      setMemberLoading(false);
    });
  }, [selectedMember]); // eslint-disable-line

  /* 팀 정보 + 팀원 목록 조회 (우리 팀 / 상대 팀 공용) */
  const fetchTeamInfo = useCallback(async (teamId) => {
    setTeamInfoLoading(true);
    setTeamInfoData(null);
    const normalType = normalizeSportsType(post?.sports_type || "");
    const [{ data: fullTeam }, { data: memberRows }] = await Promise.all([
      supabase.from("teams").select("*").eq("id", teamId).maybeSingle(),
      supabase.from("team_members").select("user_id").eq("team_id", teamId).eq("status", "accepted"),
    ]);
    if (!memberRows || memberRows.length === 0) {
      setTeamInfoData({ fullTeam, members: [] });
      setTeamInfoLoading(false);
      return;
    }
    const userIds = memberRows.map(m => m.user_id);
    const [{ data: profiles }, { data: sports }] = await Promise.all([
      supabase.from("user_profiles").select("id, nickname, profile_image, gender").in("id", userIds),
      supabase.from("user_sports").select("user_id, career_detail, is_player").in("user_id", userIds).eq("sports_type", normalType),
    ]);
    const profileMap = Object.fromEntries((profiles || []).map(p => [p.id, p]));
    const sportMap   = Object.fromEntries((sports   || []).map(sp => [sp.user_id, sp]));
    setTeamInfoData({
      fullTeam,
      members: memberRows.map(m => ({
        user_id:  m.user_id,
        isLeader: fullTeam?.user_id === m.user_id,
        profile:  profileMap[m.user_id] || null,
        sport:    sportMap[m.user_id]   || null,
      })),
    });
    setTeamInfoLoading(false);
  }, [post?.sports_type]); // eslint-disable-line

  const openTeamInfo = (team, role) => {
    setTeamInfoTarget({ team, role });
    fetchTeamInfo(team.id);
  };

  const handleAttend = async () => {
    if (attendLoading || !user?.id) return;
    setAttendLoading(true);
    if (attending) {
      await supabase.from("attendance").delete().eq("post_id", post.id).eq("user_id", user.id);
      setAttending(false);
    } else {
      await supabase.from("attendance").upsert([{
        post_id: post.id,
        team_id: myTeam?.id,
        user_id: user.id,
        status:  "attending",
      }], { onConflict: "post_id,user_id" });
      setAttending(true);
    }
    setAttendLoading(false);
  };

  const statusCfg = STATUS_CONFIG[application?.status] || STATUS_CONFIG.pending;
  const sport     = SPORTS[post?.sports_type] || { icon: "🏅", label: post?.sports_type || "-" };
  const location  = buildLocation(post);

  return (
    <div style={s.container}>
      {/* 헤더 */}
      <div style={s.header}>
        <button style={s.backBtn} onClick={onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="#E8412A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          뒤로
        </button>
        <span style={s.headerTitle}>교류전 상세</span>
        <div style={{ width: 52 }} />
      </div>

      <div style={s.body}>
        {/* 교류전 정보 카드 */}
        <div style={s.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: "#1A1A2E" }}>
              {sport.icon} {sport.label} 교류전
            </span>
            <span style={{ ...s.statusBadge, color: statusCfg.color, background: statusCfg.bg, border: `1px solid ${statusCfg.border}` }}>
              {statusCfg.label}
            </span>
          </div>
          <InfoRow label="📅 날짜" value={formatDate(post?.game_date)} />
          {location !== "-" && <InfoRow label="📍 장소" value={location} />}
        </div>

        {/* ── 우리 팀 섹션 ── */}
        <div style={s.teamCard}>
          <div style={s.teamCardHeader}>
            <TeamThumb team={myTeam} sport={sport} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={s.teamRole}>우리 팀</div>
              <div style={s.teamCardName}>{myTeam?.team_name || "-"}</div>
            </div>
            <button style={s.infoBtn} onClick={() => openTeamInfo(myTeam, "우리 팀")}>
              정보 보기
            </button>
          </div>

          {/* 참여 버튼 */}
          {user?.id ? (
            <div style={{ marginBottom: 14 }}>
              {attending && (
                <div style={{ textAlign: "center", fontSize: 13, color: "#16a34a", fontWeight: 600, marginBottom: 6 }}>
                  참여 중이에요 ✓
                </div>
              )}
              <button
                style={{ ...s.attendBtn, background: attending ? "#9ca3af" : "#E8412A", opacity: attendLoading ? 0.6 : 1, cursor: attendLoading ? "default" : "pointer" }}
                onClick={handleAttend}
                disabled={attendLoading}
              >
                {attendLoading ? "처리 중..." : attending ? "참여 취소하기" : "교류전 참여하기"}
              </button>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", marginBottom: 14 }}>
              로그인 후 참여할 수 있어요
            </div>
          )}

          <AttendeeList
            title={`우리 팀 참가 인원 (${myAttendees.length}명)`}
            attendees={myAttendees}
            loading={myLoading}
            sportsType={post?.sports_type}
            onMemberClick={setSelectedMember}
          />
          <PublicAttendeeList attendees={myAttendees} sportsType={post?.sports_type} />
        </div>

        {/* VS 구분선 */}
        <div style={s.vsDivider}>
          <div style={s.vsLine} />
          <span style={s.vsText}>VS</span>
          <div style={s.vsLine} />
        </div>

        {/* ── 상대 팀 섹션 ── */}
        <div style={s.teamCard}>
          <div style={s.teamCardHeader}>
            <TeamThumb team={hostTeam} sport={sport} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...s.teamRole, color: "#1A1A2E" }}>상대 팀</div>
              <div style={s.teamCardName}>{hostTeam?.team_name || "-"}</div>
            </div>
            <button style={s.infoBtn} onClick={() => openTeamInfo(hostTeam, "상대 팀")}>
              정보 보기
            </button>
          </div>

          <AttendeeList
            title={`상대 팀 참가 인원 (${hostAttendees.length}명)`}
            attendees={hostAttendees}
            loading={hostLoading}
            sportsType={post?.sports_type}
            onMemberClick={setSelectedMember}
          />
          <PublicAttendeeList attendees={hostAttendees} sportsType={post?.sports_type} />
        </div>
      </div>

      {/* 팀 정보 모달 */}
      {teamInfoTarget && (
        <TeamInfoModal
          role={teamInfoTarget.role}
          data={teamInfoData}
          loading={teamInfoLoading}
          sportsType={post?.sports_type}
          sport={sport}
          onMemberClick={(m) => { setTeamInfoTarget(null); setSelectedMember(m); }}
          onClose={() => setTeamInfoTarget(null)}
        />
      )}

      {/* 팀원 프로필 모달 */}
      {selectedMember && (
        <MemberProfileModal
          member={selectedMember}
          profile={memberProfile}
          sports={memberSports}
          loading={memberLoading}
          sportsType={post?.sports_type}
          onClose={() => setSelectedMember(null)}
        />
      )}
    </div>
  );
}

/* ── 서브 컴포넌트 ─────────────────────────────── */

function TeamThumb({ team, sport }) {
  const src = team?.logo_url || team?.team_photo || null;
  return src ? (
    <img src={src} alt="팀" style={s.teamThumb} />
  ) : (
    <div style={s.teamThumbFallback}>
      <span style={{ fontSize: 24 }}>{sport?.icon || "🏅"}</span>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 6 }}>
      <span style={{ fontSize: 13, color: "#9ca3af", flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#374151", fontWeight: 600, textAlign: "right", flex: 1, wordBreak: "keep-all" }}>{value}</span>
    </div>
  );
}

function ModalInfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #ebebf0" }}>
      <span style={{ fontSize: 12, color: "#9ca3af", flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#374151", fontWeight: 600, textAlign: "right", marginLeft: 8 }}>{value}</span>
    </div>
  );
}

function PublicAttendeeList({ attendees, sportsType }) {
  const publicOnes = attendees.filter(a => a.profile?.is_profile_public === true);
  if (publicOnes.length === 0) return null;
  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f3f4f6" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", marginBottom: 8 }}>공개 동의 팀원</div>
      {publicOnes.map(a => {
        const nick    = a.profile?.nickname || "?";
        const initial = nick[0]?.toUpperCase() || "?";
        const level   = formatSportDetail(sportsType, a.sport?.career_detail);
        return (
          <div key={a.user_id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#1A1A2E", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{initial}</span>
            </div>
            <div>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1A1A2E" }}>{nick}</span>
              {level && <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 6 }}>{level}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AttendeeList({ title, attendees, loading, sportsType, onMemberClick }) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 8 }}>{title}</div>
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "10px 0" }}>
          <div style={s.spinner} />
        </div>
      ) : attendees.length === 0 ? (
        <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>아직 참가 신청한 팀원이 없어요</p>
      ) : (
        attendees.map(a => (
          <AttendeeCard key={a.user_id} attendee={a} sportsType={sportsType} onClick={() => onMemberClick(a)} />
        ))
      )}
    </div>
  );
}

function AttendeeCard({ attendee, sportsType, onClick }) {
  const { profile, sport } = attendee;
  const nick    = profile?.nickname || "(닉네임 없음)";
  const initial = nick[0]?.toUpperCase() || "?";
  const level   = formatSportDetail(sportsType, sport?.career_detail);
  const detail  = [level, sport ? (sport.is_player ? "선출" : "비선출") : null].filter(Boolean).join(" · ");
  return (
    <div style={s.attendeeCard} onClick={onClick}>
      {profile?.profile_image ? (
        <img src={profile.profile_image} alt="profile" style={s.avatar} />
      ) : (
        <div style={s.avatarFallback}><span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{initial}</span></div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{nick}</div>
        {detail && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{detail}</div>}
      </div>
    </div>
  );
}

function TeamInfoModal({ role, data, loading, sportsType, sport, onMemberClick, onClose }) {
  const team    = data?.fullTeam || null;
  const members = data?.members  || [];
  const totalMembers = team ? (team.male_count || 0) + (team.female_count || 0) : 0;
  const availDays    = parseArray(team?.available_days);
  const availTimes   = parseArray(team?.available_times);
  const ageGroupStr  = parseAgeGroup(team?.age_group);

  return (
    <div style={tm.backdrop} onClick={onClose}>
      <div style={tm.sheet} onClick={e => e.stopPropagation()}>
        {/* 시트 핸들 */}
        <div style={{ width: 36, height: 4, background: "#e5e7eb", borderRadius: 2, margin: "0 auto 16px" }} />

        {/* 헤더 */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#1A1A2E" }}>{role} 정보</div>
          <button style={tm.closeBtn} onClick={onClose}>✕</button>
        </div>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
            <div style={s.spinner} />
          </div>
        ) : !team ? (
          <div style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "24px 0" }}>팀 정보를 불러올 수 없어요</div>
        ) : (
          <>
            {/* 팀 히어로 */}
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              {team.logo_url ? (
                <img src={team.logo_url} alt="팀 로고"
                  style={{ width: 60, height: 60, borderRadius: 14, objectFit: "cover", border: "2px solid #e5e7eb", marginBottom: 8 }} />
              ) : (
                <div style={{ fontSize: 36, marginBottom: 8 }}>{sport?.icon || "🏅"}</div>
              )}
              <div style={{ fontSize: 17, fontWeight: 800, color: "#1A1A2E", marginBottom: 2 }}>{team.team_name}</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 8 }}>{sport?.label || "-"}</div>
              <div style={{ display: "flex", gap: 5, justifyContent: "center", flexWrap: "wrap", marginBottom: 6 }}>
                {team.level && (
                  <span style={{ padding: "3px 10px", background: "#fff5f5", border: "1px solid #E8412A", borderRadius: 999, fontSize: 11, fontWeight: 700, color: "#E8412A" }}>
                    {team.level}
                  </span>
                )}
                {team.skill_level && (
                  <span style={{ padding: "3px 10px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 999, fontSize: 11, fontWeight: 700, color: "#0369a1" }}>
                    {team.skill_level}
                  </span>
                )}
              </div>
              {(team.match_level != null || team.manner_avg != null) && (
                <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                  {team.match_level != null && <LevelBadge level={team.match_level} />}
                  {team.manner_avg  != null && <MannerBadge score={team.manner_avg} />}
                </div>
              )}
            </div>

            {/* 팀 단체사진 */}
            {team.team_photo && (
              <img src={team.team_photo} alt="단체사진"
                style={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 10, border: "1px solid #e5e7eb", display: "block", marginBottom: 12 }} />
            )}

            {/* 팀 상세 정보 */}
            <div style={{ background: "#f5f6fa", borderRadius: 10, padding: "2px 12px", marginBottom: 12 }}>
              <ModalInfoRow label="구력 레벨"   value={formatSportLevel(team.sports_type, team.level) || "-"} />
              {team.skill_level && <ModalInfoRow label="실력" value={team.skill_level} />}
              <ModalInfoRow label="전적 레벨"   value={formatRecordLevel(team) || "-"} />
              <ModalInfoRow label="전적"         value={formatRecord(team) || "-"} />
              <ModalInfoRow label="매너 점수"    value={formatManner(team) || "-"} />
              <ModalInfoRow label="총 인원"       value={`${totalMembers}명 (남 ${team.male_count || 0} / 여 ${team.female_count || 0})`} />
              {ageGroupStr && <ModalInfoRow label="연령대"    value={ageGroupStr} />}
              {team.member_avg_career && <ModalInfoRow label="평균 구력" value={team.member_avg_career} />}
              {team.main_place && <ModalInfoRow label="주 활동 장소" value={team.main_place} />}
              {availDays.length  > 0 && <ModalInfoRow label="가능 요일" value={availDays.join(", ")} />}
              {availTimes.length > 0 && <ModalInfoRow label="가능 시간" value={availTimes.join(", ")} />}
            </div>

            {/* 팀 소개 */}
            {team.intro && (
              <div style={{ background: "#f5f6fa", borderRadius: 10, padding: "10px 12px", marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 4 }}>팀 소개</div>
                <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{team.intro}</div>
              </div>
            )}

            {/* 팀원 목록 */}
            <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 8 }}>
              팀원 ({members.length}명)
            </div>
            {members.length === 0 ? (
              <div style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "12px 0" }}>팀원이 없어요</div>
            ) : (
              members.map(m => (
                <MemberCard key={m.user_id} member={m} sportsType={sportsType} onClick={() => onMemberClick(m)} />
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}

function MemberCard({ member, sportsType, onClick }) {
  const { profile, sport, isLeader } = member;
  const nick    = profile?.nickname || "(닉네임 없음)";
  const initial = nick[0]?.toUpperCase() || "?";
  const level   = formatSportDetail(sportsType, sport?.career_detail);
  const detail  = [level, sport ? (sport.is_player ? "선출" : "비선출") : null].filter(Boolean).join(" · ");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px", background: "#f9fafb", borderRadius: 10, marginBottom: 6, border: "1px solid #f3f4f6", cursor: "pointer" }} onClick={onClick}>
      {profile?.profile_image ? (
        <img src={profile.profile_image} alt="profile" style={s.avatar} />
      ) : (
        <div style={s.avatarFallback}><span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{initial}</span></div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{nick}</span>
          {isLeader && (
            <span style={{ fontSize: 10, fontWeight: 700, color: "#E8412A", background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 999, padding: "1px 6px" }}>팀장</span>
          )}
        </div>
        {detail && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{detail}</div>}
      </div>
    </div>
  );
}

function MemberProfileModal({ member, profile, sports, loading, sportsType, onClose }) {
  const nick    = profile?.nickname || "(닉네임 없음)";
  const initial = nick[0]?.toUpperCase() || "?";
  const sp      = sports?.[0] || null;
  const spLabel = sp ? (SPORTS_LABEL_MAP[sp.sports_type] || sp.sports_type) : null;
  const spIcon  = sp ? (SPORTS_ICON_MAP[sp.sports_type] || "🏅") : null;
  const spLevel = sp ? formatSportDetail(sp.sports_type, sp.career_detail) : null; // eslint-disable-line
  const gender  = profile?.gender === "male" ? "남성" : profile?.gender === "female" ? "여성" : null;

  return (
    <div style={mp.backdrop} onClick={onClose}>
      <div style={mp.sheet} onClick={e => e.stopPropagation()}>
        <button style={mp.closeBtn} onClick={onClose}>✕</button>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingBottom: 20, borderBottom: "1px solid #f3f4f6", marginBottom: 16 }}>
          {profile?.profile_image ? (
            <img src={profile.profile_image} alt="profile" style={mp.avatar} />
          ) : (
            <div style={mp.avatarFallback}><span style={{ fontSize: 28, fontWeight: 800, color: "#fff" }}>{initial}</span></div>
          )}
          <div style={{ fontSize: 20, fontWeight: 800, color: "#1A1A2E", marginBottom: 8 }}>{nick}</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center" }}>
            {gender && <span style={mp.chip}>{gender}</span>}
            {profile?.region && <span style={mp.chip}>📍 {profile.region}</span>}
          </div>
          {profile?.awards && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 6 }}>🏆 {profile.awards}</div>}
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#9ca3af", marginBottom: 10 }}>선수 정보</div>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "16px 0" }}>
            <div style={s.spinner} />
          </div>
        ) : !sp ? (
          <div style={{ fontSize: 13, color: "#9ca3af", textAlign: "center", padding: "12px 0" }}>등록된 종목 프로필이 없어요</div>
        ) : (
          <div style={{ background: "#F5F5F7", borderRadius: 10, padding: "0 14px" }}>
            <MPRow label="종목"     value={`${spIcon} ${spLabel}`} />
            {spLevel && <MPRow label="레벨" value={spLevel} />}
            <MPRow label="선수 출신" value={sp.is_player ? "선출" : "비선출"} />
            {gender && <MPRow label="성별" value={gender} />}
          </div>
        )}
      </div>
    </div>
  );
}

function MPRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 0", borderBottom: "1px solid #ebebf0" }}>
      <span style={{ fontSize: 12, color: "#9ca3af", flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#374151", fontWeight: 600, textAlign: "right", marginLeft: 8 }}>{value}</span>
    </div>
  );
}

/* ── 스타일 ─────────────────────────────────────── */
const s = {
  container:       { minHeight: "100dvh", background: "#F5F5F7", display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto", fontFamily: "'Noto Sans KR', sans-serif" },
  header:          { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "calc(env(safe-area-inset-top) + 12px) 20px 12px", background: "#fff", borderBottom: "1px solid #edf0f3", position: "sticky", top: 0, zIndex: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" },
  backBtn:         { display: "flex", alignItems: "center", gap: 4, fontSize: 14, fontWeight: 600, color: "#E8412A", background: "none", border: "none", cursor: "pointer", padding: 0, fontFamily: "inherit" },
  headerTitle:     { fontSize: 16, fontWeight: 700, color: "#111827" },
  body:            { flex: 1, padding: "16px 16px calc(100px + env(safe-area-inset-bottom))", display: "flex", flexDirection: "column", gap: 12 },

  card:            { background: "#fff", borderRadius: 12, padding: "16px", boxShadow: "0 1px 6px rgba(0,0,0,0.05)", border: "1px solid #e5e7eb" },
  statusBadge:     { fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 999, flexShrink: 0 },

  teamCard:        { background: "#fff", borderRadius: 12, padding: "16px", boxShadow: "0 1px 6px rgba(0,0,0,0.05)", border: "1px solid #e5e7eb" },
  teamCardHeader:  { display: "flex", alignItems: "center", gap: 12, marginBottom: 14 },
  teamRole:        { fontSize: 11, fontWeight: 700, color: "#E8412A", marginBottom: 2 },
  teamCardName:    { fontSize: 16, fontWeight: 700, color: "#1A1A2E" },
  teamThumb:       { width: 52, height: 52, borderRadius: 12, objectFit: "cover", border: "1px solid #e5e7eb", flexShrink: 0 },
  teamThumbFallback: { width: 52, height: 52, borderRadius: 12, background: "#f5f6fa", border: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },

  infoBtn:         { padding: "5px 10px", background: "#f5f6fa", color: "#374151", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer", flexShrink: 0, fontFamily: "inherit" },
  attendBtn:       { width: "100%", padding: "14px", color: "#fff", border: "none", borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },

  vsDivider:       { display: "flex", alignItems: "center", gap: 12, padding: "4px 0" },
  vsLine:          { flex: 1, height: 2, background: "#e5e7eb", borderRadius: 1 },
  vsText:          { fontSize: 28, fontWeight: 900, color: "#1A1A2E", letterSpacing: 2 },

  attendeeCard:    { display: "flex", alignItems: "center", gap: 10, padding: "10px", background: "#f9fafb", borderRadius: 10, marginBottom: 6, border: "1px solid #f3f4f6", cursor: "pointer" },
  avatar:          { width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 },
  avatarFallback:  { width: 36, height: 36, borderRadius: "50%", background: "#E8412A", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  spinner:         { width: 22, height: 22, border: "3px solid #f0f0f0", borderTop: "3px solid #E8412A", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
};

/* 팀 정보 모달 — 하단 시트 */
const tm = {
  backdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 300 },
  sheet:    { background: "#fff", borderRadius: "16px 16px 0 0", width: "100%", maxWidth: 430, padding: "12px 20px calc(40px + env(safe-area-inset-bottom))", maxHeight: "88vh", overflowY: "auto", fontFamily: "'Noto Sans KR', sans-serif", position: "relative" },
  closeBtn: { position: "absolute", top: 16, right: 16, background: "none", border: "none", fontSize: 18, color: "#6b7280", cursor: "pointer", lineHeight: 1 },
};

/* 팀원 프로필 모달 — 센터 */
const mp = {
  backdrop:     { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 350, padding: "0 20px" },
  sheet:        { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 360, padding: "24px 20px 28px", position: "relative", maxHeight: "85vh", overflowY: "auto", fontFamily: "'Noto Sans KR', sans-serif", boxShadow: "0 8px 32px rgba(0,0,0,0.20)" },
  closeBtn:     { position: "absolute", top: 16, right: 16, background: "none", border: "none", fontSize: 18, color: "#6b7280", cursor: "pointer", lineHeight: 1 },
  avatar:       { width: 72, height: 72, borderRadius: "50%", objectFit: "cover", marginBottom: 12 },
  avatarFallback: { width: 72, height: 72, borderRadius: "50%", background: "#E8412A", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 },
  chip:         { fontSize: 11, fontWeight: 600, padding: "2px 8px", background: "#f3f4f6", borderRadius: 999, color: "#6b7280" },
};
